import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";

// Delete for records outside the generic modules (Daily Work tasks, reports,
// attendance, material requests). SuperAdmin only -- the server enforces the
// same rule; for everyone else this renders nothing.
export default function DeleteButton({ endpoint, what = "record", onDeleted, size = "sm", iconOnly = false }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (user?.role !== "SuperAdmin") return null;

  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.delete(endpoint);
      toast.success(`${what.charAt(0).toUpperCase()}${what.slice(1)} deleted`);
      setOpen(false);
      onDeleted?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="ghost" size={iconOnly ? "icon" : size} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }} title={`Delete ${what}`} data-testid="delete-button">
        <Trash2 className="h-4 w-4" />{!iconOnly && <span className="ml-1.5">Delete</span>}
      </Button>
      <AlertDialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {what}?</AlertDialogTitle>
            <AlertDialogDescription>This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={run} disabled={busy} className="bg-rose-600 hover:bg-rose-700">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
